import { PdfLayout } from '../PdfLayout';
import { Database, Filter, Sigma, Download, HardDrive, ServerCog } from 'lucide-react';

const benefits = [
  {
    icon: Database,
    title: 'Time-Series Database on the Appliance',
    text: 'Tag changes are written to an InfluxDB time-series database running on the appliance itself — no server to buy, no cloud required.',
  },
  {
    icon: Filter,
    title: 'Record Only What Changes',
    text: 'Switch history on per tag. A point is stored when the value or quality changes, after scaling and deadband, so storage goes to real changes.',
  },
  {
    icon: Sigma,
    title: 'Hourly and Daily Rollups',
    text: 'Min, max, average, count and last for every recorded tag, each hour and each day, kept for years by default. Charts of two weeks or more use them automatically.',
  },
  {
    icon: Download,
    title: 'CSV Export & Historian API',
    text: 'Export a tag\'s time window to CSV, or let MES and BI tools read up to 10 tags per request, raw or aggregated, from a JSON endpoint.',
  },
  {
    icon: HardDrive,
    title: 'Rides Through Outages',
    text: 'With store & forward, changes made while the database is down are buffered and replayed with their original timestamps.',
  },
  {
    icon: ServerCog,
    title: 'Your Retention, Your SQL',
    text: 'Set how many days to keep, for all tags or per tag. Write history to SQL Server, MySQL, PostgreSQL, Oracle or ODBC instead of, or as well as, InfluxDB.',
  },
];

export function Historian() {
  return (
    <PdfLayout
      category="Data Storage"
      headline="Record Every Change with the"
      headlineHighlight="Built-In Historian"
      description="Tag changes from every machine recorded on the appliance. Chart up to six tags together over any window, drag across a chart to zoom in, aggregate them, export them, or hand them to your reporting tools — with the data staying on your network."
      accentColor="#ca8a04"
      benefits={benefits}
    >
      <HistorianMockup />
    </PdfLayout>
  );
}

function HistorianMockup() {
  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Tag Historian</span>
      </div>

      {/* Time range bar */}
      <div className="glass-card p-1.5 mb-2 flex items-center gap-1 justify-center">
        {['15m', '1h', '6h', '24h', '7d', '30d'].map((r, i) => (
          <span
            key={r}
            className={`text-[7px] px-2 py-0.5 rounded-md font-medium ${
              i === 3
                ? 'bg-brand-primary/10 text-brand-primary'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            {r}
          </span>
        ))}
      </div>

      {/* Main trend chart */}
      <div className="glass-card p-2 mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[7px] text-text-muted">Spindle RPM · CNC-01</span>
          <span className="text-[7px] font-medium" style={{ color: '#ca8a04' }}>4,218 RPM</span>
        </div>
        <svg viewBox="0 0 240 60" className="w-full h-12">
          {/* Grid lines */}
          <line x1="0" y1="15" x2="240" y2="15" stroke="#e5e7ef" strokeWidth="0.5" />
          <line x1="0" y1="30" x2="240" y2="30" stroke="#e5e7ef" strokeWidth="0.5" />
          <line x1="0" y1="45" x2="240" y2="45" stroke="#e5e7ef" strokeWidth="0.5" />

          {/* Data line */}
          <polyline
            points="0,35 15,33 30,28 45,30 60,22 75,24 90,20 105,25 120,18 135,15 150,20 165,22 180,12 195,15 210,10 225,14 240,8"
            fill="none"
            stroke="#ca8a04"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="0,35 15,33 30,28 45,30 60,22 75,24 90,20 105,25 120,18 135,15 150,20 165,22 180,12 195,15 210,10 225,14 240,8 240,60 0,60"
            fill="url(#histGrad)"
            strokeWidth="0"
          />

          <defs>
            <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ca8a04" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#ca8a04" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
        <div className="flex justify-between text-[5px] text-text-muted mt-0.5">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>Now</span>
        </div>
      </div>

      {/* Second trend - temperature */}
      <div className="glass-card p-2 mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[7px] text-text-muted">Coolant Temp · CNC-01</span>
          <span className="text-[7px] font-medium" style={{ color: '#3b82f6' }}>142°F</span>
        </div>
        <svg viewBox="0 0 240 35" className="w-full h-7">
          <polyline
            points="0,25 30,22 60,20 90,18 120,19 150,16 180,17 210,14 240,15"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="0,25 30,22 60,20 90,18 120,19 150,16 180,17 210,14 240,15 240,35 0,35"
            fill="url(#histGrad2)"
            strokeWidth="0"
          />
          <defs>
            <linearGradient id="histGrad2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-1.5">
        <div className="glass-card p-1.5 text-center">
          <div className="text-[9px] font-bold" style={{ color: '#ca8a04' }}>2</div>
          <div className="text-[6px] text-text-muted">Tags Selected</div>
        </div>
        <div className="glass-card p-1.5 text-center">
          <div className="text-[9px] font-bold text-brand-primary">186</div>
          <div className="text-[6px] text-text-muted">Tags Recording</div>
        </div>
        <div className="glass-card p-1.5 text-center">
          <div className="text-[9px] font-bold text-status-running">90 days</div>
          <div className="text-[6px] text-text-muted">Retention</div>
        </div>
      </div>
    </div>
  );
}
